import { withStyles } from '@material-ui/core';

import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import {
  AUTH_UPDATE_IMPORT_TEMPLATE_TABS,
  AuthUpdateTemplateEditor,
  AuthUpdateTemplateEditorButton,
  AuthUpdateTemplateSelector,
  SelectedAuthUpdateImportTemplate,
  getAuthUpdateImportResultErrors,
  getAuthUpdateImportTemplateId,
  getAuthUpdateImportTemplateList,
  hasAuthUpdateImportRowErrors,
} from './importAuthUpdates.components';
import { CLIENT_CUSTOM_FIELDS_STATUS } from './importAuthFiles.components';
import {
  GET_AUTH_UPDATE_IMPORT_CLIENT_CUSTOM_FIELDS_FAILURE,
  GET_AUTH_UPDATE_IMPORT_TEMPLATES_FAILURE,
  GET_AUTH_UPDATE_IMPORT_TEMPLATE_FAILURE,
  POST_AUTH_UPDATE_IMPORT_TEMPLATE_DRYRUN_FAILURE,
  POST_AUTH_UPDATE_IMPORT_TEMPLATE_FAILURE,
  POST_AUTH_UPDATE_IMPORT_TEMPLATE_SUBMIT_FAILURE,
  PUT_AUTH_UPDATE_IMPORT_TEMPLATE_FAILURE,
} from './importAuthUpdates.types';
import { Grid, Paper, Stack } from '@mui/material';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import { getAllEnumerations } from 'modules/enumerations/store/enumerationActions';
import {
  getAuthUpdateImportClientCustomFields,
  getAuthUpdateImportTemplate,
  getAuthUpdateImportTemplates,
  postAuthUpdateImportTemplate,
  putAuthUpdateImportTemplate,
  submitAuthUpdateImportTemplateFile,
  uploadAuthUpdateImportTemplateFile,
} from './importAuthUpdates.actions';
import { getClients } from 'modules/clients/clients.actions';
import { getInternalUsers } from 'modules/common/common.actions';
import { showToast } from 'modules/layout/layout.actions';

// Stable fallback so the template editor does not rebuild its columns on every render
const NO_INTERNAL_USERS = [];

const styles = theme => ({
  root: {
    margin: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(1.5),
  },
});

function ImportAuthUpdatesView(props) {
  const {
    classes,
    clients,
    enumerations,
    getAllEnumerations: loadAllEnumerations,
    getAuthUpdateImportClientCustomFields: loadAuthUpdateImportClientCustomFields,
    getAuthUpdateImportTemplate: loadAuthUpdateImportTemplate,
    getAuthUpdateImportTemplates: loadAuthUpdateImportTemplates,
    getClients: loadClients,
    getInternalUsers: loadInternalUsers,
    internalUsers,
    postAuthUpdateImportTemplate: createAuthUpdateImportTemplate,
    putAuthUpdateImportTemplate: updateAuthUpdateImportTemplate,
    showToast: showToastMessage,
    submitAuthUpdateImportTemplateFile: submitAuthUpdateImportTemplateFileForImport,
    uploadAuthUpdateImportTemplateFile: dryRunAuthUpdateImportTemplateFile,
  } = props;

  const [authUpdateImportTemplates, setAuthUpdateImportTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editorTemplate, setEditorTemplate] = useState(null);
  const [selectedTemplateFile, setSelectedTemplateFile] = useState(null);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isDryRunLoading, setIsDryRunLoading] = useState(false);
  const [isImportSubmitting, setIsImportSubmitting] = useState(false);
  const [dryRunResults, setDryRunResults] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [selectedTemplateTab, setSelectedTemplateTab] = useState(AUTH_UPDATE_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS);
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
  const [selectedTemplateClientCustomFields, setSelectedTemplateClientCustomFields] = useState([]);
  const [selectedTemplateClientCustomFieldsStatus, setSelectedTemplateClientCustomFieldsStatus] = useState(CLIENT_CUSTOM_FIELDS_STATUS.IDLE);

  // Resolves to the client's custom fields (standard and custom), or null when they could not be loaded
  const loadClientCustomFields = useCallback(async clientRecordId => {
    const response = await loadAuthUpdateImportClientCustomFields(clientRecordId);

    if (response.type === GET_AUTH_UPDATE_IMPORT_CLIENT_CUSTOM_FIELDS_FAILURE) {
      showToastMessage('Failed to load the client\'s custom fields. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return null;
    }

    // The API returns no content when the client has no fields
    return Array.isArray(response.response) ? response.response : [];
  }, [loadAuthUpdateImportClientCustomFields, showToastMessage]);

  const selectedTemplateClientRecordId = selectedTemplate?.clientRecordId;

  const fetchAuthUpdateImportTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);

    const response = await loadAuthUpdateImportTemplates();

    setIsLoadingTemplates(false);

    if (response.type === GET_AUTH_UPDATE_IMPORT_TEMPLATES_FAILURE) {
      showToastMessage('Failed to load auth update import templates. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setAuthUpdateImportTemplates(getAuthUpdateImportTemplateList(response.response));
  }, [loadAuthUpdateImportTemplates, showToastMessage]);

  const handleTemplateSelected = useCallback(async templateId => {
    setSelectedTemplateId(templateId);
    setSelectedTemplate(null);
    setEditorTemplate(null);
    setSelectedTemplateFile(null);
    setDryRunResults(null);
    setImportResults(null);
    setSelectedTemplateTab(AUTH_UPDATE_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS);

    if (!templateId) return;

    setIsLoadingTemplate(true);

    const response = await loadAuthUpdateImportTemplate(templateId);

    setIsLoadingTemplate(false);

    if (response.type === GET_AUTH_UPDATE_IMPORT_TEMPLATE_FAILURE) {
      showToastMessage('Failed to load auth update import template. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setSelectedTemplate(response.response);
  }, [loadAuthUpdateImportTemplate, showToastMessage]);

  const handleFileSelected = file => {
    setSelectedTemplateFile(file);
    setDryRunResults(null);
    setImportResults(null);
    setSelectedTemplateTab(AUTH_UPDATE_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS);
  };

  const openTemplateEditor = () => {
    setEditorTemplate(selectedTemplate || null);
    setTemplateEditorOpen(true);
  };

  const handleTemplateSaved = async payload => {
    const templateId = getAuthUpdateImportTemplateId(payload);
    const response = editorTemplate
      ? await updateAuthUpdateImportTemplate(templateId, payload)
      : await createAuthUpdateImportTemplate(payload);
    const failureType = editorTemplate ? PUT_AUTH_UPDATE_IMPORT_TEMPLATE_FAILURE : POST_AUTH_UPDATE_IMPORT_TEMPLATE_FAILURE;

    if (response.type === failureType) {
      showToastMessage('Failed to save auth update import template. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return false;
    }

    showToastMessage('Auth update import template saved successfully.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
    await fetchAuthUpdateImportTemplates();

    const savedTemplateId = getAuthUpdateImportTemplateId(response.response);
    if (savedTemplateId) {
      // A changed template invalidates results from the previous mappings
      setSelectedTemplateId(String(savedTemplateId));
      setSelectedTemplate(response.response);
      setDryRunResults(null);
      setImportResults(null);
      setSelectedTemplateTab(AUTH_UPDATE_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS);
    }

    setEditorTemplate(null);
    setTemplateEditorOpen(false);
    return true;
  };

  const handleDryRun = async () => {
    const templateId = selectedTemplateId || getAuthUpdateImportTemplateId(selectedTemplate);

    if (!templateId || !selectedTemplateFile || isDryRunLoading || isImportSubmitting) return;

    setIsDryRunLoading(true);
    setImportResults(null);

    const response = await dryRunAuthUpdateImportTemplateFile(templateId, selectedTemplateFile);

    setIsDryRunLoading(false);

    if (response.type === POST_AUTH_UPDATE_IMPORT_TEMPLATE_DRYRUN_FAILURE) {
      setDryRunResults({ errors: response.messages ?? ['Failed to upload auth update import file. Please try again.'] });
      setSelectedTemplateTab(AUTH_UPDATE_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS);
      showToastMessage('Auth update dry run failed. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setDryRunResults(response.response);
    setSelectedTemplateTab(AUTH_UPDATE_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS);

    if (getAuthUpdateImportResultErrors(response.response).length > 0) {
      showToastMessage('Auth update dry run completed with errors.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    showToastMessage('Auth update dry run completed successfully.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
  };

  const handleSubmitImport = async () => {
    const templateId = selectedTemplateId || getAuthUpdateImportTemplateId(selectedTemplate);

    if (
      !templateId
      || !selectedTemplateFile
      || !dryRunResults
      || getAuthUpdateImportResultErrors(dryRunResults).length > 0
      || isDryRunLoading
      || isImportSubmitting
    ) return;

    setIsImportSubmitting(true);

    const response = await submitAuthUpdateImportTemplateFileForImport(templateId, selectedTemplateFile);

    setIsImportSubmitting(false);

    if (response.type === POST_AUTH_UPDATE_IMPORT_TEMPLATE_SUBMIT_FAILURE) {
      setImportResults({ errors: response.messages ?? ['Failed to submit auth update import file. Please try again.'] });
      setSelectedTemplateTab(AUTH_UPDATE_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS);
      showToastMessage('Auth update submit failed. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setImportResults(response.response);
    setSelectedTemplateTab(AUTH_UPDATE_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS);

    if (getAuthUpdateImportResultErrors(response.response).length > 0 || hasAuthUpdateImportRowErrors(response.response)) {
      showToastMessage('Auth update import completed with errors.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    showToastMessage('Auth update import submitted successfully.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
  };

  // The template view needs the client's fields to label custom field mappings and flag ones that are no longer available
  useEffect(() => {
    if (!selectedTemplateClientRecordId) {
      setSelectedTemplateClientCustomFields([]);
      setSelectedTemplateClientCustomFieldsStatus(CLIENT_CUSTOM_FIELDS_STATUS.IDLE);
      return undefined;
    }

    let isCurrentClient = true;
    setSelectedTemplateClientCustomFieldsStatus(CLIENT_CUSTOM_FIELDS_STATUS.LOADING);

    loadClientCustomFields(selectedTemplateClientRecordId).then(customFields => {
      if (!isCurrentClient) return;

      setSelectedTemplateClientCustomFields(customFields ?? []);
      setSelectedTemplateClientCustomFieldsStatus(customFields ? CLIENT_CUSTOM_FIELDS_STATUS.LOADED : CLIENT_CUSTOM_FIELDS_STATUS.ERROR);
    });

    return () => {
      isCurrentClient = false;
    };
  }, [loadClientCustomFields, selectedTemplate, selectedTemplateClientRecordId]);

  useEffect(() => {
    fetchAuthUpdateImportTemplates();
  }, [fetchAuthUpdateImportTemplates]);

  useEffect(() => {
    if (!enumerations) {
      loadAllEnumerations();
    }
  }, [enumerations, loadAllEnumerations]);

  useEffect(() => {
    if (!Array.isArray(clients) || clients.length === 0) {
      loadClients();
    }
  }, [clients, loadClients]);

  // Team member override values are picked from the internal users
  useEffect(() => {
    if (!internalUsers) {
      loadInternalUsers();
    }
  }, [internalUsers, loadInternalUsers]);

  return (
    <div className={`${classes.root} p-2`}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <AuthUpdateTemplateSelector
                  isLoading={isLoadingTemplates}
                  templates={authUpdateImportTemplates}
                  selectedTemplateId={selectedTemplateId}
                  onTemplateSelected={handleTemplateSelected}
                />
              </Stack>
              <AuthUpdateTemplateEditorButton isEditMode={Boolean(selectedTemplate)} disabled={isLoadingTemplate} onClick={openTemplateEditor} />
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          {(selectedTemplateId || isLoadingTemplate) && (
            <SelectedAuthUpdateImportTemplate
              activeTab={selectedTemplateTab}
              clientCustomFields={selectedTemplateClientCustomFields}
              clientCustomFieldsStatus={selectedTemplateClientCustomFieldsStatus}
              clients={clients}
              dryRunResults={dryRunResults}
              enumerations={enumerations}
              importResults={importResults}
              internalUsers={internalUsers || NO_INTERNAL_USERS}
              isDryRunLoading={isDryRunLoading}
              isImportSubmitting={isImportSubmitting}
              isLoading={isLoadingTemplate}
              selectedTemplateFile={selectedTemplateFile}
              template={selectedTemplate}
              onDryRun={handleDryRun}
              onFileSelected={handleFileSelected}
              onSubmitImport={handleSubmitImport}
              onTabChange={setSelectedTemplateTab}
            />
          )}
        </Grid>
      </Grid>
      <AuthUpdateTemplateEditor
        clients={clients}
        enumerations={enumerations}
        initialTemplate={editorTemplate}
        internalUsers={internalUsers || NO_INTERNAL_USERS}
        isEditMode={Boolean(editorTemplate)}
        open={templateEditorOpen}
        onClose={() => setTemplateEditorOpen(false)}
        onLoadClientCustomFields={loadClientCustomFields}
        onSaveTemplate={handleTemplateSaved}
      />
    </div>
  );
}

ImportAuthUpdatesView.propTypes = {
  classes: PropTypes.object.isRequired,
  clients: PropTypes.arrayOf(PropTypes.object),
  enumerations: PropTypes.object,
  getAllEnumerations: PropTypes.func.isRequired,
  getAuthUpdateImportClientCustomFields: PropTypes.func.isRequired,
  getAuthUpdateImportTemplate: PropTypes.func.isRequired,
  getAuthUpdateImportTemplates: PropTypes.func.isRequired,
  getClients: PropTypes.func.isRequired,
  getInternalUsers: PropTypes.func.isRequired,
  internalUsers: PropTypes.arrayOf(PropTypes.object),
  postAuthUpdateImportTemplate: PropTypes.func.isRequired,
  putAuthUpdateImportTemplate: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  submitAuthUpdateImportTemplateFile: PropTypes.func.isRequired,
  uploadAuthUpdateImportTemplateFile: PropTypes.func.isRequired,
};

ImportAuthUpdatesView.defaultProps = {
  clients: [],
  enumerations: null,
  internalUsers: null,
};

const mapStateToProps = state => ({
  clients: state.clients?.unmappedClients || [],
  enumerations: state.enumerations ? state.enumerations.allEnums : null,
  internalUsers: state.common?.internalUsers ?? null,
});

export default compose(
  withStyles(styles),
  withRouter,
  connect(mapStateToProps, {
    getAllEnumerations,
    getAuthUpdateImportClientCustomFields,
    getAuthUpdateImportTemplate,
    getAuthUpdateImportTemplates,
    getClients,
    getInternalUsers,
    postAuthUpdateImportTemplate,
    putAuthUpdateImportTemplate,
    showToast,
    submitAuthUpdateImportTemplateFile,
    uploadAuthUpdateImportTemplateFile,
  }),
)(ImportAuthUpdatesView);
